import React, { useState } from 'react';
import { Phone, Building2, ArrowRight, CheckCircle2, X, Shield, AlertCircle, QrCode } from 'lucide-react';
import { submitRemoteCustomerApplication } from '../lib/formService';
import { useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

interface RemoteCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  bankName: string;
  bankLogo?: string;
  redirectUrl?: string;
}

const RemoteCustomerModal: React.FC<RemoteCustomerModalProps> = ({
  isOpen,
  onClose,
  bankName,
  bankLogo,
  redirectUrl
}) => {
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [applicationMethod, setApplicationMethod] = useState<'sms' | 'qr'>('sms');
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [errors, setErrors] = useState<{
    phone?: string;
    consent?: string;
    submit?: string;
  }>({});

  if (!isOpen) return null;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhone(value);
      if (errors.phone) {
        setErrors(prev => ({ ...prev, phone: undefined }));
      }
    }
  };

  const formatPhone = (value: string) => {
    if (!value) return '';
    const match = value.match(/^(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})$/);
    if (!match) return value;
    return match
      .slice(1)
      .filter(Boolean)
      .join(' ');
  };

  const validateStep = () => {
    const newErrors: typeof errors = {};

    if (applicationMethod === 'sms') {
      if (!phone || phone.length < 10) {
        newErrors.phone = 'Lütfen geçerli bir telefon numarası girin (10 haneli)';
      }
    }

    if (!consent) {
      newErrors.consent = 'Devam etmek için KVKK metnini onaylamanız gerekmektedir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate a unique application URL that can be encoded in QR
  const generateApplicationUrl = () => {
    // Create a base URL for the application
    const baseUrl = window.location.origin;
    // Create a unique identifier for this application
    const uniqueId = Date.now().toString(36) + Math.random().toString(36).substring(2);
    
    // Use redirectUrl if provided, otherwise generate a default one
    if (redirectUrl) {
      return redirectUrl;
    }
    
    // Return the full application URL
    return `${baseUrl}/application/${bankName.toLowerCase().replace(/\s+/g, '-')}/${uniqueId}`;
  };

  const applicationUrl = generateApplicationUrl();

  const handleQRCodeClick = () => {
    setApplicationMethod('qr');
    setShowQRCode(true);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setIsSubmitting(true);
    setErrors(prev => ({ ...prev, submit: undefined }));

    try {
      const result = await submitRemoteCustomerApplication({
        bank_name: bankName,
        phone_number: phone,
        application_method: applicationMethod,
        kvkk_consent: consent,
        marketing_consent: marketingConsent,
        source_page: location.pathname,
        source_section: 'remote_customer_modal'
      });

      if (result.success) {
        setStep(2);
      } else {
        setErrors({ submit: result.error || 'Bir hata oluştu. Lütfen tekrar deneyin.' });
      }
    } catch (error) {
      setErrors({ submit: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-800 mb-3">
                {bankName} Müşterisi Olun
              </h2>
              <p className="text-slate-600">
                Şubeye gitmeden hemen {bankName} müşterisi olabilirsiniz
              </p>
            </div>

            {/* Bank Info */}
            {bankLogo && (
              <div className="bg-gradient-to-br from-[#F7BA45]/10 to-slate-50 rounded-xl p-6 border border-[#F7BA45]/20">
                <div className="flex items-center gap-4">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <img src={bankLogo} alt={bankName} className="h-10 object-contain" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-slate-800">{bankName}</div>
                    <div className="text-sm text-slate-600">Uzaktan Müşteri Edinimi</div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Application Method */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Başvuru Yöntemi
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setApplicationMethod('sms');
                      setShowQRCode(false);
                    }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      applicationMethod === 'sms'
                        ? 'border-[#F7BA45] bg-[#F7BA45]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Phone size={24} className={applicationMethod === 'sms' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                    <span className={`text-sm font-medium ${applicationMethod === 'sms' ? 'text-[#F7BA45]' : 'text-slate-600'}`}>
                      SMS ile Başvur
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={handleQRCodeClick}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      applicationMethod === 'qr'
                        ? 'border-[#F7BA45] bg-[#F7BA45]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <QrCode size={24} className={applicationMethod === 'qr' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                    <span className={`text-sm font-medium ${applicationMethod === 'qr' ? 'text-[#F7BA45]' : 'text-slate-600'}`}>
                      QR ile Başvur
                    </span>
                  </button>
                </div>
              </div>

              {/* QR Code Display */}
              {showQRCode && (
                <div className="flex flex-col items-center justify-center p-4 bg-white border border-slate-200 rounded-xl">
                  <div className="mb-4 p-2 bg-white rounded-lg shadow-md">
                    <QRCodeSVG 
                      value={applicationUrl}
                      size={200}
                      bgColor={"#FFFFFF"}
                      fgColor={"#000000"}
                      level={"L"}
                      includeMargin={false}
                    />
                  </div>
                  <p className="text-sm text-slate-600 text-center">
                    Bu QR kodu telefonunuzla tarayarak başvuru formuna ulaşabilirsiniz.
                  </p>
                  <a 
                    href={applicationUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
                  >
                    Başvuru Sayfasına Git
                    <ArrowRight size={16} />
                  </a>
                </div>
              )}

              {/* Phone Input - Only show if SMS method is selected */}
              {applicationMethod === 'sms' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Telefon Numarası *
                  </label>
                  <div className="flex gap-2">
                    <div className="w-20">
                      <div className="flex items-center h-12 px-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-600 font-medium">
                        +90
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                        <input
                          type="text"
                          value={formatPhone(phone)}
                          onChange={handlePhoneChange}
                          placeholder="(___) ___ __ __"
                          className={`w-full h-12 pl-10 pr-4 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                            errors.phone ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                  {errors.phone && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.phone}
                    </p>
                  )}
                </div>
              )}

              {/* Consent Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-start">
                    <div className="bg-white border-2 rounded-lg w-6 h-6 flex flex-shrink-0 justify-center items-center
                      focus-within:border-[#F7BA45] transition-colors">
                      <input
                        type="checkbox"
                        className="opacity-0 absolute"
                        checked={consent}
                        onChange={e => {
                          setConsent(e.target.checked);
                          if (errors.consent) {
                            setErrors(prev => ({ ...prev, consent: undefined }));
                          }
                        }}
                      />
                      <CheckCircle2 
                        className={`${consent ? 'block' : 'hidden'} h-4 w-4 text-[#F7BA45] pointer-events-none`}
                      />
                    </div>
                  </div>
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                    <a href="#" className="text-[#F7BA45] hover:text-[#e5ac3d] font-medium">KVKK Aydınlatma Metni</a>'ni okudum ve onaylıyorum. *
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-start">
                    <div className="bg-white border-2 rounded-lg w-6 h-6 flex flex-shrink-0 justify-center items-center
                      focus-within:border-[#F7BA45] transition-colors">
                      <input
                        type="checkbox"
                        className="opacity-0 absolute"
                        checked={marketingConsent}
                        onChange={e => setMarketingConsent(e.target.checked)}
                      />
                      <CheckCircle2 
                        className={`${marketingConsent ? 'block' : 'hidden'} h-4 w-4 text-[#F7BA45] pointer-events-none`}
                      />
                    </div>
                  </div>
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                    Kampanya ve fırsatlardan haberdar olmak istiyorum.
                  </span>
                </label>
              </div>
              
              {errors.consent && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {errors.consent}
                </p>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="text-center space-y-8">
            <div className="w-20 h-20 bg-[#F7BA45]/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={40} className="text-[#F7BA45]" />
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3">
                Başvurunuz Alındı!
              </h2>
              <p className="text-slate-600 max-w-sm mx-auto">
                {applicationMethod === 'sms' 
                  ? `${bankName} müşterisi olma başvurunuz alınmıştır. Kısa süre içinde telefonunuza SMS ile bilgilendirme mesajı gönderilecektir.`
                  : `${bankName} müşterisi olma başvurunuz alınmıştır. Kısa süre içinde telefonunuza QR kod içeren bir bilgilendirme mesajı gönderilecektir.`
                }
              </p>
            </div>

            <button
              onClick={onClose}
              className="inline-flex items-center justify-center px-8 py-4 bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 font-medium rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Tamam
            </button>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div 
          className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute right-4 top-4 p-2 hover:bg-slate-100 rounded-full transition-colors z-10"
          >
            <X size={20} className="text-slate-500" />
          </button>

          {/* Content */}
          <div className="p-8">
            {renderStep()}

            {/* Submit Button */}
            {step === 1 && (
              <div className="mt-8">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || (applicationMethod === 'qr' && !consent)}
                  className="w-full bg-[#F7BA45] hover:bg-[#e5ac3d] disabled:bg-slate-300 text-slate-900 font-medium h-14 rounded-xl 
                    transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      BAŞVURU YAP
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>

                {/* Error Messages */}
                {errors.submit && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                      <AlertCircle size={16} />
                      <span>{errors.submit}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoteCustomerModal;